// Feature 009: Deal Operating System
// Party Detail View Component
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React, { useState, useEffect } from 'react';
import PageLoading from '../shared/PageLoading';
import Button from '../shared/Button';
import StatusBadge from '../shared/StatusBadge';

const PartyDetail = ({ partyId }) => {
  const [party, setParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchParty();
  }, [partyId]);

  const fetchParty = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/parties/${partyId}`);
      if (!response.ok) {
        throw new Error('Party not found');
      }
      const data = await response.json();
      setParty(data);
    } catch (err) {
      console.error('Error fetching party:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPartyType = (type) => {
    if (!type) return 'Unknown';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (loading) {
    return <PageLoading message="Loading Party" />;
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-[#c47474]">{error}</p>
          <Button href="/parties" variant="secondary" className="mt-4">
            Back to Parties
          </Button>
        </div>
      </div>
    );
  }

  if (!party) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-[rgba(255,251,247,0.5)]">Party not found</p>
          <Button href="/parties" variant="secondary" className="mt-4">
            Back to Parties
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl mx-auto p-8">
        {/* Document Header */}
        <div className="mb-8 pb-6 border-b border-[rgba(255,251,247,0.2)]">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <span
                className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-2"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                PARTY PTY.{party.id}
              </span>
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold text-[#fffbf7] tracking-[0.08em] uppercase">
                  {party.name}
                </h1>
                <StatusBadge status={party.status} />
              </div>
            </div>
            <div className="flex gap-3">
              <Button href={`/parties/${party.id}/edit`}>Edit</Button>
              <Button href="/parties" variant="secondary">
                Back to Parties
              </Button>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="space-y-6">
          {/* Party Information Section */}
          <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
            <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
              <div className="flex items-center gap-4">
                <span
                  className="text-xs text-[#a1c6a2] tracking-wider"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  01
                </span>
                <span
                  className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  Party Information
                </span>
              </div>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-[200px_1fr] gap-4">
                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Name
                </dt>
                <dd className="text-sm text-[#fffbf7]">{party.name}</dd>

                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Party Type
                </dt>
                <dd className="text-sm text-[rgba(255,251,247,0.6)]">
                  {formatPartyType(party.party_type)}
                </dd>

                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Status
                </dt>
                <dd className="text-sm text-[rgba(255,251,247,0.6)] capitalize">{party.status}</dd>
              </dl>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
            <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
              <div className="flex items-center gap-4">
                <span
                  className="text-xs text-[#a1c6a2] tracking-wider"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  02
                </span>
                <span
                  className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  Contact Information
                </span>
              </div>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-[200px_1fr] gap-4">
                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Contact Person
                </dt>
                <dd className="text-sm text-[rgba(255,251,247,0.6)]">
                  {party.contact_name || <span className="italic text-[rgba(255,251,247,0.3)]">Not provided</span>}
                </dd>

                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Title
                </dt>
                <dd className="text-sm text-[rgba(255,251,247,0.6)]">
                  {party.contact_title || <span className="italic text-[rgba(255,251,247,0.3)]">Not provided</span>}
                </dd>

                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Email
                </dt>
                <dd className="text-sm">
                  {party.contact_email ? (
                    <a href={`mailto:${party.contact_email}`} className="text-[#a1c6a2] hover:underline">
                      {party.contact_email}
                    </a>
                  ) : (
                    <span className="italic text-[rgba(255,251,247,0.3)]">Not provided</span>
                  )}
                </dd>

                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Phone
                </dt>
                <dd className="text-sm text-[rgba(255,251,247,0.6)]" style={{ fontFamily: "'GT Pressura Mono', monospace" }}>
                  {party.contact_phone || <span className="italic" style={{ fontFamily: "inherit" }}>Not provided</span>}
                </dd>

                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Address
                </dt>
                <dd className="text-sm text-[rgba(255,251,247,0.6)] whitespace-pre-line">
                  {party.address || <span className="italic text-[rgba(255,251,247,0.3)]">Not provided</span>}
                </dd>
              </dl>
            </div>
          </div>

          {/* Owned IP Objects Section */}
          <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
            <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
              <div className="flex items-center gap-4">
                <span
                  className="text-xs text-[#a1c6a2] tracking-wider"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  03
                </span>
                <span
                  className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  Owned IP Objects ({party.owned_ip_objects?.length || 0})
                </span>
              </div>
            </div>
            <div className="p-6">
              {party.owned_ip_objects && party.owned_ip_objects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {party.owned_ip_objects.map((ipObject) => (
                    <a
                      key={ipObject.id}
                      href={`/ip_objects/${ipObject.id}`}
                      className="block p-4 border border-[rgba(255,251,247,0.1)] hover:border-[#a1c6a2] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#fffbf7]">{ipObject.name}</span>
                        <StatusBadge status={ipObject.status} size="small" />
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[rgba(255,251,247,0.4)] italic">
                  No IP objects owned by this party
                </p>
              )}
            </div>
          </div>

          {/* Ownership Details Section */}
          {party.ip_ownerships && party.ip_ownerships.length > 0 && (
            <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
              <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
                <div className="flex items-center gap-4">
                  <span
                    className="text-xs text-[#a1c6a2] tracking-wider"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    04
                  </span>
                  <span
                    className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Ownership Details
                  </span>
                </div>
              </div>
              <div className="p-6 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[rgba(255,251,247,0.1)]">
                      <th className="text-left text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase py-3 pr-4">
                        IP Object
                      </th>
                      <th className="text-left text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase py-3 pr-4">
                        Type
                      </th>
                      <th className="text-left text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase py-3 pr-4">
                        Percentage
                      </th>
                      <th className="text-left text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase py-3 pr-4">
                        Acquired
                      </th>
                      <th className="text-left text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase py-3">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {party.ip_ownerships.map((ownership) => (
                      <tr key={ownership.id} className="border-b border-[rgba(255,251,247,0.05)]">
                        <td className="py-3 pr-4">
                          <a
                            href={`/ip_objects/${ownership.ip_object?.id}`}
                            className="text-sm text-[#a1c6a2] hover:underline"
                          >
                            {ownership.ip_object?.name}
                          </a>
                        </td>
                        <td className="py-3 pr-4 text-sm text-[rgba(255,251,247,0.6)] capitalize">
                          {ownership.ownership_type?.replace(/_/g, ' ')}
                        </td>
                        <td className="py-3 pr-4 text-sm text-[rgba(255,251,247,0.6)]" style={{ fontFamily: "'GT Pressura Mono', monospace" }}>
                          {ownership.ownership_percentage ? `${ownership.ownership_percentage}%` : '-'}
                        </td>
                        <td className="py-3 pr-4 text-sm text-[rgba(255,251,247,0.6)]">
                          {formatDate(ownership.acquired_date)}
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-1 text-[10px] tracking-wider uppercase ${
                            ownership.relinquished_date
                              ? 'bg-[rgba(196,116,116,0.2)] text-[#c47474]'
                              : 'bg-[rgba(161,198,162,0.2)] text-[#a1c6a2]'
                          }`}>
                            {ownership.relinquished_date ? 'Relinquished' : 'Active'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Timestamps Section */}
          <div className="border border-[rgba(255,251,247,0.1)] bg-[#0a0a0a]">
            <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
              <div className="flex items-center gap-4">
                <span
                  className="text-xs text-[#a1c6a2] tracking-wider"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  05
                </span>
                <span
                  className="text-[10px] text-[#fffbf7] tracking-[0.2em] uppercase"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  Timestamps
                </span>
              </div>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-[200px_1fr] gap-4">
                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Created
                </dt>
                <dd className="text-sm text-[rgba(255,251,247,0.6)]">
                  {formatDate(party.created_at)}
                </dd>

                <dt className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.15em] uppercase">
                  Last Updated
                </dt>
                <dd className="text-sm text-[rgba(255,251,247,0.6)]">
                  {formatDate(party.updated_at)}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartyDetail;
